import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AlertTriangle, ChevronDown, ChevronRight, Loader2, Mail, MessageSquare, Radio, RefreshCw,
    Search,
} from 'lucide-react';

import { getCommunications, type CommunicationRowDto } from '@/api/client';
import { SECTION_HEADER } from '@/lib/utils';
import { useRailSlotClose } from '@/lib/railSlot';
import {
    commsMatchesFocus, focusBasisLabel, focusScopeLabel, focusBlockedReason, type RailFocus,
} from '@/lib/railFocus';
import { onBoard, boardScopeNote } from '@/lib/boardScope';

/**
 * COMMUNICATIONS — what went out, and whether it arrived (#263).
 *
 * Pete, 2026-08-26: *"I think we should have a Communication slider below labor
 * that shows us all the communications as they go out, especially in Automated
 * mode."* … *"Or a turn on/off button in the actual heading of the slider."* …
 * *"To just view all comms."*
 *
 * ---- ⭐ WHY THIS EARNS ITS PLACE IN THE RAIL ------------------------------
 *
 * ⚠️ THE AUTOMATED TEXTS SCREEN COUNTS SENDS, WHICH IS A DIFFERENT QUESTION. It
 * read **0 sent / 0 failed in seven days** with automation switched on, and that
 * number cannot distinguish "nothing was due" from "everything is being refused"
 * from "the engine is broken". ⭐ A list of what actually left the building
 * settles it at a glance — and covers email too, which no other screen does.
 *
 * ---- ⛔ THE ON/OFF SWITCH IS IN THE HEADER, AND IT REALLY IS OFF -----------
 *
 * ⚠️ PETE MOVED IT HERE HIMSELF. His first idea was a visibility setting on the
 * Automated Texts config screen; he then said *"Or a turn on/off button in the
 * actual heading of the slider"*, which is better — a control for a panel belongs
 * on the panel, not two screens away where nobody will find it again.
 *
 * ⛔ AND "OFF" MEANS NO REQUESTS AT ALL, not a hidden div that keeps polling. A
 * switch that quietly costs the same as being on is a switch that lies. Off, this
 * panel is one row of chrome and zero network traffic.
 *
 * ⚠️ THE CHOICE IS REMEMBERED PER BROWSER. A scheduler who turned it off does not
 * want it back tomorrow, and a preference that resets is a preference nobody
 * trusts. ⛔ Wrapped in try/catch: a browser with site data blocked must render
 * the panel, not throw on first paint.
 *
 * ---- ⛔ NOTHING HERE CAN SEND ANYTHING ------------------------------------
 *
 * There is no resend button and there must not be one. Every message in the
 * platform goes through Pipeline's notification engine (arch §1B.1); a second
 * path onto a driver's phone is a second path to get consent, dedup and the audit
 * trail wrong.
 *
 * ⛔ AND THE NUMBERS ARRIVE ALREADY MASKED — "Tony ····4417" — because the server
 * masks before composing the response. ⚠️ This panel sits in a side rail on a
 * screen that faces a lobby. Do not add a control that asks for the raw value.
 */

/**
 * ---- ⛔⛔ ~~THE ON/OFF SWITCH~~ — REMOVED 2026-09-08, MOVED TO CONFIG ---------
 *
 * > Pete: *"if we have active/inactive checkboxes in a config screen, the ON
 * > button in Communications Card probably needs to be removed so it just follows
 * > the config screen."* And, confirming after it was queried: *"Remove it and
 * > put that feature in the config screen."*
 *
 * ⚠️ ~~`dock.commsPanel.on` IN localStorage~~, ~~`readStoredOn`~~,
 * ~~`writeStoredOn`~~ and ~~`toggleOn`~~ are struck rather than quietly deleted,
 * because THE SWITCH WAS NOT REDUNDANT — it answered a different question from
 * the one the config screen answers, and the two were merged on purpose:
 *
 *   · ~~per PERSON, per browser~~: am *I* watching this feed right now?
 *   · per FACILITY (migration 172): does this site use Communications at all?
 *
 * ⛔ SO THE COST IS REAL AND IT IS RECORDED HERE RATHER THAN DISCOVERED LATER:
 * this panel now POLLS FOR EVERY SCHEDULER at any facility where the card is
 * switched on, every 20 seconds, whether anyone is reading it or not. The old
 * header argued *"a switch that quietly costs the same as being on is a switch
 * that lies"* — the answer now is that there is no switch to lie, and the cost
 * moved to a screen an administrator controls.
 *
 * ⭐ IF THAT COST EVER BITES, THE FIX IS NOT TO BRING THE BADGE BACK. It is to
 * make the panel poll only while `open`, which is per-person state that already
 * exists three lines below and costs nobody a decision.
 */

/**
 * ⛔⛔ THESE THREE SAT BETWEEN `STORAGE_KEY` AND THE STORAGE HELPERS, AND THE
 * EDIT THAT REMOVED THE BADGE TOOK THEM WITH IT (2026-09-08).
 *
 * ⚠️ The splice was written as "everything from `STORAGE_KEY` up to the outcome
 * vocabulary" — a span chosen by its ENDPOINTS, on the assumption that what lay
 * between them was all badge machinery. Three constants with nothing to do with
 * the badge were sitting in the middle of it.
 *
 * ⭐⭐ AND NOTHING SHORT OF `tsc` SAW IT. The file still PARSED — `HOURS` is a
 * perfectly valid identifier, it simply no longer referred to anything — so the
 * syntax check passed, the deploy script's string guards passed, and `npm run
 * build` then failed with six `TS2304 Cannot find name`.
 *
 * ⛔ SAME SHAPE AS THE `onDone: load` TDZ BUG: **a cut that leaves valid syntax
 * behind is invisible to every check except a real type-check.** A guard that
 * asserts what a file CONTAINS can never notice what a file has LOST.
 *
 * ⚠️ SO THEY ARE PARKED HERE TOGETHER RATHER THAN SCATTERED BACK where they
 * were. The next person to delete a block bounded by two landmarks should have
 * to read this first.
 */

/** ⚠️ A rolling window, not a calendar day. See the route's comment. */
const HOURS = 24;
const TAKE = 50;

/** ⭐ Live enough to watch an automated send land; slow enough to be free. */
const POLL_MS = 20_000;

/**
 * ⭐ THE OUTCOME VOCABULARY IS THE ENGINE'S, TRANSLATED ONCE.
 *
 * ⚠️ 'suppressed' MEANS HE REPLIED STOP, AND THAT IS THE SYSTEM WORKING — it must
 * not be painted red beside a genuine failure, or a scheduler chases a delivery
 * that was correctly withheld. ⛔ 'deduped' is likewise a success: the message was
 * already sent and the engine declined to send it twice.
 */
function outcomeLook(outcome: string): { label: string; cls: string; bad: boolean } {
    const o = (outcome || '').toLowerCase();
    if (o === 'sent') return { label: 'Sent', cls: 'bg-emerald-100 text-emerald-900', bad: false };
    if (o === 'deduped') return { label: 'Already sent', cls: 'bg-slate-100 text-slate-700', bad: false };
    if (o === 'suppressed') return { label: 'Opted out', cls: 'bg-amber-100 text-amber-900', bad: false };
    if (o === 'failed' || o === 'threw') return { label: 'Failed', cls: 'bg-rose-100 text-rose-900', bad: true };
    // ⚠️ AN UNKNOWN OUTCOME IS SHOWN AS ITSELF, not folded into "failed". Pipeline
    // owns this vocabulary and may grow it; guessing would make a new success look
    // like a new fault.
    return { label: outcome || 'Unknown', cls: 'bg-slate-100 text-slate-700', bad: false };
}

/** ⚠️ The type code is for us; this is for the person reading the rail. */
function typeLabel(typeCode: string): string {
    switch (typeCode) {
        case 'DOCK_APPT_DOOR_DRIVER': return 'Dock door';
        case 'DOCK_APPT_REMINDER': return 'Reminder';
        case 'DOCK_APPT_STATUS_DRIVER': return 'Status (driver)';
        default: {
            // ⭐ Readable rather than blank. An unmapped code still tells a story.
            const tail = typeCode.replace(/^DOCK_/, '').replace(/_/g, ' ').toLowerCase();
            return tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : 'Message';
        }
    }
}

function hhmm(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

interface CommunicationsPanelProps {
    /**
     * ⛔ THE FACILITY ON SCREEN, AND IT IS NOT NULLABLE (2026-08-27). It used to be
     * `string | null`, where null meant "every facility" — see the note by the
     * removed switch. A site never sees another site's messages (§1K), so there is
     * no value here that means "all".
     */
    facilityCode: string;
    /** ⭐ DECISION 215 — the calendar day, `YYYY-MM-DD`, facility-local. Required by
     *  the party scope; the appointment and driver scopes are keyed on an id and
     *  need no date. */
    date: string;
    getToken: () => Promise<string>;
    /** Bumped by the rail's Refresh so this reloads with everything else. */
    refreshKey?: number;
    /**
     * ⭐⭐ WHAT THE SCHEDULER IS POINTING AT, OR null FOR EVERYTHING.
     *
     * > Pete, 2026-09-10: *"it will show all the communications for that driver
     * > card. Same idea of appointments … We will need an ALL icon to reset it,
     * > next to the search icon."*
     *
     * ⛔ IT LATCHES — see `Calendar`. This panel never clears it; only ALL does.
     */
    focus?: RailFocus | null;
    /**
     * ---- ⭐⭐ WHAT THE BOARD IS SHOWING (Pete, 2026-09-25) ------------------
     * > *"it doesn't filter the drivers/client/carrier/consignee and the right
     * > side panels to those appointments"* -> *"same with all the status' too"*.
     * ⛔ A SECOND AXIS, NOT A NARROWER FOCUS. `focus` is a hover or a selection;
     * this is the board's filter set. Folding one into the other would make a
     * mouse-over change what the filters mean. See `lib/boardScope.ts`.
     * ⚠️ `undefined` MEANS NO BOUND. An empty Set is a real answer.
     */
    boardApptIds?: ReadonlySet<string>;
    onClearFocus?: () => void;
    /**
     * ---- ⭐⭐ THE CROSS-HIGHLIGHT, BOTH WAYS (Pete, 2026-09-23) --------------
     *
     * > *"when I hover over the communications it doesn't highlight the
     * > appointment. If I hover over the appointment, it highlights the
     * > communication panel associated - but not sure it highlights all the
     * > communications asscoiated the appointment."*
     *
     * ⛔⛔ THE PANEL WAS NEVER WIRED FOR EITHER DIRECTION. `LabourPanel` receives
     * `onHoverAppointment` seventeen lines above this panel's mount in
     * `CheckInPanel.tsx`; this one received neither prop, so hovering a message
     * marked nothing and nothing marked a message.
     *
     * ⚠️ SENT WITH ORIGIN `'rail'`, ALWAYS. See the note on the row itself - the
     * origin is the difference between marking the board and re-scoping the list
     * this row lives in.
     */
    onHoverAppointment?: (appointmentId: string | null, origin?: 'grid' | 'rail') => void;
    /**
     * ---- ⭐⭐⭐ SINGLE CLICK LATCHES (Pete, 2026-09-27) ----------------------
     *
     * > *"Hover should narrow down the calendar, single click makes it stay. I thought
     * > that is how we have it on all the panels?"*
     *
     * ⛔⛔ IT WAS NOT, AND NOT HERE EITHER. Like Labour, this panel reported a hover
     * for the ring and had no click handler at all. The full gesture existed only on
     * the party chips and the driver cards.
     *
     * ⭐ IT SENDS AN **ID**. A message row knows an `appointmentId` and nothing more;
     * `CheckInPanel` holds the day's appointments and builds the focus for all three
     * rails, so there is one label rule rather than three.
     */
    onChooseAppointment?: (id: string | null) => void;
    /**
     * ⭐ THE OTHER DIRECTION: which load the cursor is on, wherever it is.
     *
     * ⛔ EVERY ROW THAT MATCHES IS MARKED, NOT THE FIRST. Pete's doubt was the
     * right one - a load has several messages and a single-row answer would look
     * like a working feature while hiding most of the traffic it was asked about.
     *   ** A HIGHLIGHT THAT STOPS AT THE FIRST MATCH IS A SEARCH, NOT A
     *      HIGHLIGHT. **
     *
     * ⚠️ IT MARKS WHAT IS LOADED, AND CANNOT MARK WHAT IS NOT. The feed is capped
     * at TAKE over a rolling window, so a load whose messages fall outside it has
     * nothing here to mark. That is the list's bound, not this feature's - but it
     * means an unmarked panel is not proof a load was never texted.
     */
    highlightAppointmentId?: string | null;
}

export function CommunicationsPanel({
    /**
     * ⛔⤴ ~~`onClearFocus`~~ NO LONGER DESTRUCTURED (increment 3, 2026-09-19). The
     * per-panel `ALL` button is gone; ONE master reset at the top of the rail clears
     * the focus for all of them. ⚠️ The PROP stays in the interface so the page's
     * wiring is unchanged and so a panel can regain its own control without a
     * signature change — but nothing here may render one, or there would be five
     * controls for one action again.
     */
    facilityCode, date, getToken, refreshKey, focus = null, boardApptIds,
    onHoverAppointment, onChooseAppointment, highlightAppointmentId = null,
}: CommunicationsPanelProps) {
    /**
     * ⛔⛔ COLLAPSED BY DEFAULT (2026-08-27). Pete: *"Lets make the Sliders all collapsed
     * by default with that arrow on the left side except driver checkins. So the user has
     * to expand the arrow to see other items."*
     *
     * ⚠️ THE RAIL HAS BEEN LOSING A FIGHT FOR HEIGHT ALL DAY — the phone line and the
     * Needs-license checkbox (#310) were both moved to claw back single lines. Three
     * sections open at once is the real cause: the check-ins, which are the reason the
     * rail exists, get whatever is left over.
     *
     * ⭐ SO THE DEFAULT IS NOW "one row of chrome", and each section carries its counts
     * IN ITS HEADER — which is why folding them costs nothing: a folded Communications
     * still says how many failed, and a folded Labor still says how many are in progress.
     * ⛔ Sections whose headers were silent could not have been folded this way.
     *
     * ⚠️ DRIVER CHECK-INS ARE NOT ONE OF THESE and stay expanded. They are the panel.
     */
    const [open, setOpen] = useState(false);

    /* ⭐ TELL THE RAIL. One line, no prop contracts touched — see
       `lib/railSlot.ts` for why this reports upward instead of being lifted.
       ⚠️ The rail uses it to give an OPEN card the leftover space and to move
       it above the closed ones; a card that never reported would simply keep
       today's behaviour rather than break. */
    /**
     * ⛔⛔ ~~THE ACCORDION~~ REVERTED 2026-09-10, SAME DAY, BY PETE: *"now they
     * don't work. none of them expand. Put it back."*
     *
     * ⚠️ THE FAULT WAS A RENDER/COMMIT RACE, not the idea. Clicking a chevron set
     * `open = true`; on THAT render the rail had not been told yet, so `openCodes`
     * was still empty and the yield signal read TRUE for the panel that had just
     * opened. Both effects then ran in the same commit — the report went up and the
     * panel closed itself. It closed on the way open, every time.
     *
     * ⛔⛔ AND THE SIMULATION THAT PASSED IT TESTED THE WRONG THING. It modelled
     * report → reduce → read as a settle-to-fixpoint loop and converged in two
     * passes. React captures the yield value during RENDER, before the report has
     * updated the context — the simulation had no render/commit ordering in it at
     * all, so it could not have caught this. §4.14, in the check written to prove
     * the accordion safe.
     *
     * ⭐ WHEN IT COMES BACK: report SYNCHRONOUSLY from the click, alongside
     * `setOpen`, so the next render already has the new `openCodes` and the stale
     * window does not exist. An effect is the wrong place for it.
     */
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('COMMS', open, setOpen);

    /**
     * ⛔⛔ THE "ALL FACILITIES" SWITCH WAS HERE AND IS GONE (2026-08-27).
     *
     * Pete: *"Only the facility you are logged into should be able to see their own
     * communications. Shouldnt be able to see others."*
     *
     * ⚠️ IT CAME FROM MISREADING HIS EARLIER *"to just view all comms"* — which
     * meant every KIND of message, not every SITE. This panel lists messages sent
     * to drivers and carrier contacts, so widening it across facilities showed one
     * site every other site's people (§1K).
     *
     * ⛔ AND THE REAL FIX IS ON THE SERVER, not here. `facilityCode` is now REQUIRED
     * by the route — a missing one is a 400, not "everything" — because a UI that no
     * longer asks is not a server that no longer answers.
     */
    const [rows, setRows] = useState<CommunicationRowDto[]>([]);
    const [unattributed, setUnattributed] = useState(0);
    const [capped, setCapped] = useState(false);
    const [unavailable, setUnavailable] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * ⭐⭐ THE FILTERS (2026-08-27). Pete: *"lets put checkbox filters on Labor
     * slider and Communication Slider so we can narrow down/filter… communications
     * should have by dock door, cell number search? and reference/po/container??"*
     *
     * ⛔ ONE BOX, NOT FOUR — and the reason is what a scheduler is actually doing.
     * They are holding a picking ticket or a bill of lading and typing whichever
     * number is printed on it. Making them first choose WHICH KIND of number they
     * have is asking a question they should not have to answer; the main search box
     * settled this same argument already.
     *
     * ⛔⛔ AND DOCK DOOR IS DELIBERATELY NOT ONE OF THEM. `dbo.NotificationSent` has
     * no door, and most of what this panel lists never had one: a reminder goes out
     * hours ahead of any door assignment, and a status email has nothing to do with
     * a door at all. A door filter would silently hide most of the feed and look
     * like the panel was broken. ⭐ The door lives on the LABOUR panel, where it is
     * genuinely the axis.
     *
     * ⚠️ FILTERED IN THE BROWSER, NOT REFETCHED. The window is at most 50 rows and
     * they are already here — a round trip per keystroke would lag behind the box.
     */
    const [filter, setFilter] = useState('');
    const [failedOnly, setFailedOnly] = useState(false);

    /**
     * ⭐ THE FILTER STRIP IS CLOSED BY DEFAULT, BEHIND THE SEARCH ICON — the same
     * shape Driver Check-ins uses (Pete, 2026-09-09: *"Should utilzie the search
     * icon just like driver checkins"*). Three rails share one column and this
     * panel's job is the list, not the box above it.
     */
    const [showFilters, setShowFilters] = useState(false);

    /**
     * ⛔⛔ IS A FILTER ON RIGHT NOW? This drives the amber dot on the icon, and it
     * is the whole reason hiding the strip is safe.
     *
     * ⚠️ A FILTER THAT IS ON WHILE THE STRIP IS CLOSED IS INVISIBLE, and an
     * invisible filter on THIS panel is how somebody concludes a message was never
     * sent — the exact wrong conclusion it was built to prevent. The dot is not
     * decoration; it is what makes the space saving honest.
     */
    const filtersActive = filter.trim().length > 0 || failedOnly;

    /**
     * ⛔ GUARDS AGAINST A SLOW RESPONSE LANDING AFTER A NEWER ONE. The poll and a
     * manual refresh can overlap, and without this the older answer wins whenever
     * it happens to finish second — a list that goes backwards in time on its own.
     */
    /**
     * ⭐ WHAT THE FILTER MATCHES, in one place so the box and the count agree.
     *
     * ⛔ THE MASKED RECIPIENT IS SEARCHABLE AND THE RAW NUMBER IS NOT. Pete asked
     * for *"cell number search"* — the numbers arrive here already masked to their
     * last four ("Tony ····4417"), masked on the SERVER because this rail faces a
     * lobby. ⚠️ So a search matches what is ON SCREEN: the last four digits, or the
     * driver's name. Un-masking to support a full-number search would undo a
     * deliberate decision for the sake of a filter.
     */
    const haystack = (r: CommunicationRowDto): string => [
        r.appointmentNumber, r.poNumber, r.referenceNumber, r.containerNumber,
        typeLabel(r.typeCode), r.outcome,
        ...(r.recipients ?? []),
    ].filter(Boolean).join(' ').toLowerCase();

    /**
     * ---- ⭐⭐ THE HOVER SCOPE, APPLIED BEFORE THE TYPED FILTER --------------
     *
     * ⛔ TWO NARROWINGS, AND THEY MUST NOT BE CONFLATED. The scope is what the
     * scheduler POINTED at; the filter is what she TYPED. Folding them into one
     * predicate would make "17 hidden by the filter" count rows the filter never
     * saw, and that number is the panel's own honesty check.
     *
     * ⚠️ `blockedReason` IS NOT "NO RESULTS". It means this focus cannot be
     * scoped at all — a driver with neither a load nor a phone — and the panel
     * says so in words rather than rendering a clean empty list that reads as
     * "nothing was ever sent to this man".
     */
    const blockedReason = focus ? focusBlockedReason(focus, 'comms') : null;
    const focused = !focus
        ? rows
        : (blockedReason ? [] : rows.filter(r => commsMatchesFocus(r, focus)));
    /**
     * ⛔ BETWEEN THE FOCUS AND THE TYPED FILTER, so `hidden` below keeps meaning
     * "hidden by the typed filter" — the separation this file's own note above
     * insists on, and the reason its Clear button can honour what it says.
     * ⚠️ A MESSAGE WITH NO APPOINTMENT SURVIVES: a text to a driver nobody has
     * matched yet is not the board's to hide. See `lib/boardScope.ts`.
     */
    const inScope = focused.filter(r => onBoard(boardApptIds, r.appointmentId));
    const boardHidden = focused.length - inScope.length;

    const needle = filter.trim().toLowerCase();
    const visible = inScope.filter(r => {
        if (failedOnly && !outcomeLook(r.outcome).bad) return false;
        if (!needle) return true;
        return haystack(r).includes(needle);
    });
    /** ⚠️ How many the filter is hiding — never a silently shorter list. */
    const hidden = inScope.length - visible.length;
    /** ⚠️ How many the SCOPE is hiding, counted separately for the same reason. */
    const outOfScope = focus ? rows.length - inScope.length : 0;

    const seq = useRef(0);


    /**
     * ---- ⭐⭐⭐ #68 — ONE APPOINTMENT MEANS **ALL** OF ITS ROWS (2026-09-22) ----
     *
     * > Pete, 2026-09-21: *"When singling out an appointment, it should always show
     * > all the labor, communications, paperwork, history on that appointment -
     * > looks like its filtered on time or something."*
     *
     * ⛔⛤ IT WAS NOT THIS PANEL FILTERING. The route returned a window and the rows
     * outside it were never sent, so no amount of client-side filtering could have
     * shown them.
     *   ** A PANEL THAT FILTERS WHAT IT RECEIVED CANNOT SHOW WHAT IT WAS NOT SENT,
     *      AND FROM THE OUTSIDE THE TWO LOOK IDENTICAL. **
     *
     * ⛔⛔ ONLY WHEN THE FOCUS NAMES EXACTLY ONE LOAD. `client`, `carrier` and
     * `consignee` cover every appointment that party has today (decision 188), and
     * sending one of their ids would silently narrow the panel to a single truck —
     * a worse answer than the window, and one that looks correct.
     * ⚠️ A `driver` focus counts only once he is MATCHED. An unmatched driver has a
     * null appointment and is scoped by the phone tail against what is on screen,
     * which still needs the window.
     *   ** A NARROWING THAT LOOKS LIKE AN ANSWER IS WORSE THAN NO NARROWING. **
     */
    const scopeAppointmentId = focus
        && (focus.kind === 'appointment' || focus.kind === 'driver')
        ? focus.appointmentId
        : null;

    /**
     * ---- ⭐⭐⭐ DECISION 215 — THE PARTY SCOPE (2026-09-22) ------------------
     *
     * > Pete: *"All the other ones besides appointment should show their individual
     * > comms, history, etc. so you get a complete picture and a sub picture."*
     *
     * ⛔⛔ THE FAULT THIS FIXES RUNS THE OPPOSITE WAY FROM #68. There the panel
     * showed too FEW, because rows were never sent. Here a client, carrier or
     * consignee focus sent NO scope at all — the condition above yields `null` for
     * those kinds — so the panel fell back to the facility's rolling feed: **every
     * message at the site, under a label naming one client.**
     *   ** A PANEL THAT SHOWS EVERYTHING UNDER A LABEL THAT NAMES ONE THING IS NOT
     *      UNFILTERED, IT IS MISLABELLED — AND THAT IS THE HARDER FAULT TO SEE,
     *      BECAUSE A FULL LIST LOOKS LIKE A WORKING ONE. **
     *
     * ⚠️ `partyCode` IS REQUIRED IN THE CONDITION, NOT ASSUMED FROM THE KIND. A
     * focus built from a card that carries no code cannot be scoped, and sending
     * two thirds of the triple makes the server treat it as absent — silently back
     * to the whole site. So this asks for the code itself.
     */
    const scopeParty = focus
        && (focus.kind === 'client' || focus.kind === 'carrier' || focus.kind === 'consignee')
        && focus.partyCode
        ? { kind: focus.kind, code: focus.partyCode }
        : null;

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setLoading(true);
        try {
            const token = await getToken();
            const page = await getCommunications(token, {
                facilityCode,
                /* ⭐ DECISION 215: the party triple, or nothing. `getCommunications`
                   only puts them on the query string when all three are present. */
                partyKind: scopeParty?.kind ?? null,
                partyCode: scopeParty?.code ?? null,
                date,
                hours: HOURS,
                take: TAKE,
                /* ⭐ #68. Null = the rolling feed, exactly as before. */
                appointmentId: scopeAppointmentId,
            });
            if (mine !== seq.current) return;
            setRows(page.rows);
            setUnattributed(page.unattributed);
            setCapped(page.capped);
            setUnavailable(page.unavailable);
            setError(null);
        } catch (err) {
            if (mine !== seq.current) return;
            // ⚠️ NAMED, NOT SWALLOWED. This panel exists because a silent zero was
            // indistinguishable from a broken feed; repeating that here would be
            // the same mistake one level up.
            setError(err instanceof Error ? err.message : 'The feed could not be read.');
        } finally {
            if (mine === seq.current) setLoading(false);
        }
        /* ⛔ THE SCOPE IS A DEPENDENCY. Without it the callback closes over the
           focus it had when the panel mounted, and picking a different load would
           re-render the list while re-reading the previous one.
             ** A CONSTANT CLOSED OVER IN A CALLBACK BECOMES A BUG THE DAY THE
                CALLBACK GAINS A SECOND SOURCE. ** */
    /**
     * ---- ⛔⛔ PRIMITIVES IN THE DEPENDENCY ARRAY, NEVER THE OBJECT ------------
     *
     * ⚠️ `scopeParty` IS AN OBJECT LITERAL built during render, so it has a NEW
     * IDENTITY EVERY TIME. Putting it here would be: effect runs -> setState ->
     * re-render -> new object -> effect runs. An infinite loop that presents as a
     * frozen tab, not as an error anybody can read.
     * ⭐ Its two fields are strings, so they compare by value and change only when
     * the scope really changes. `scopeAppointmentId` is already a string for the
     * same reason.
     *   ** A DEPENDENCY ARRAY COMPARES BY IDENTITY. ANYTHING BUILT DURING RENDER
     *      IS ALWAYS NEW, SO LIST WHAT IT IS MADE OF, NOT WHAT IT IS. **
     *
     * ⛔ AND `date` BELONGS HERE. It is read inside `load` and it is what the party
     * scope is keyed on — without it, moving the calendar would leave a client's
     * messages showing yesterday's loads. That is precisely the fault `HistoryPanel`
     * shipped with earlier today, caught only because a type error sent me to the
     * same function.
     */
    }, [facilityCode, date, getToken, scopeAppointmentId, scopeParty?.kind, scopeParty?.code]);

    useEffect(() => { void load(); }, [load, refreshKey]);

    useEffect(() => {
        // ⚠️ THE TIMER IS UNCONDITIONAL NOW. It used to stop when the badge said
        //    Off; the card's very presence is the switch since 2026-09-08 — see the
        //    struck note at the top, including what to do if this cost ever bites.
        const t = setInterval(() => { void load(); }, POLL_MS);
        return () => clearInterval(t);
    }, [load]);


    const failures = rows.filter(r => outcomeLook(r.outcome).bad).length;

    return (
        <section className="flex min-h-0 flex-col border-t">
            {/**
              * ⭐ THE SWITCH LIVES IN THE HEADING, WHICH IS WHERE PETE PUT IT.
              *
              * ⚠️ IT IS NOT INSIDE THE COLLAPSE. Collapsing hides the list; the
              * switch decides whether there IS a list. A control that disappears
              * when you fold the thing it controls cannot be used to unfold it.
              *
              * ⛔ AND IT IS NOT A <button> INSIDE A <button>. The header row is a
              * div; the chevron and the switch are separate buttons within it.
              * Nesting them is invalid HTML and the inner click is swallowed.
              */}
            {/* ⭐ STICKY — see ScanFolderPanel. ⛔ AND THE ON/OFF SWITCH LIVES IN
                HERE, so it must stay reachable however far the feed is scrolled. */}
                {/**
                  * ⭐⭐ THE HEADER IS A SIBLING OF THE SCROLLER, NOT INSIDE IT (2026-08-27).
                  *
                  * Pete: *"when you scroll those sections, the 'Scanned Paperwork' title
                  * bar should not disappear."*
                  *
                  * ⛔ IT WAS ALREADY `sticky top-0` AND THAT WAS THE PROBLEM — sticky is a
                  * promise about a scroll container, and this rail has three of them
                  * nested inside each other. A sticky header pins to whichever ancestor
                  * happens to be scrolling, so it holds until a wheel event chains to a
                  * DIFFERENT one and then rides away with it.
                  *
                  * ⭐ A HEADER THAT IS NOT INSIDE ANY SCROLLER CANNOT SCROLL AWAY. The
                  * root is now a flex column, the header is `shrink-0`, and only the BODY
                  * below it scrolls. That is a structural guarantee rather than a CSS
                  * promise, and it does not care how many scrollers surround it.
                  *
                  * ⚠️ `sticky` WAS REMOVED RATHER THAN LEFT IN "just in case". A sticky
                  * that no longer does anything is a line that will be trusted by the next
                  * person to read it.
                  */}
            <div className={`shrink-0 bg-card flex w-full items-center gap-2 px-3 py-2 ${SECTION_HEADER}`}>
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="flex min-w-0 items-center gap-2 text-left"
                    aria-expanded={open}
                >
                    {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    {/* ⭐ ALWAYS EMERALD NOW. It used to grey out when the badge said
                        Off, and there is no Off. ⚠️ Labor wears the same mark in purple —
                        Pete, 2026-09-08: *"lets make the Labor icon purple like the
                        communications icon is green. Want consistency."* */}
                    <Radio className="h-3.5 w-3.5 text-emerald-600" />
                    {/* ⭐ Matches the Radio glyph above — see `PaperworkSliver`'s note. */}
                    <span className="text-sm font-medium text-emerald-600">Communications</span>
                    {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </button>

                {/**
                  * ---- ⭐⭐ THE SEARCH ICON, MATCHING DRIVER CHECK-INS ------------
                  *
                  * > Pete, 2026-09-09: *"Should utilzie the search icon just like
                  * > driver checkins … Same with communications"* and *"put the
                  * > search button next to communications so it matches Driver
                  * > Checkin"*
                  *
                  * ⛔ THE FILTER ROW USED TO BE PERMANENTLY OPEN — a search box and a
                  * tick-box, always, at the top of a panel whose whole job is the
                  * list underneath. Three rails share one column; a row that is
                  * always there costs its height on every card below it, forever.
                  *
                  * ⚠️ IT IS **OUTSIDE** THE TITLE BUTTON, not inside it. A `<button>`
                  * inside a `<button>` is invalid HTML — the browser closes the
                  * outer one early and the second control is silently unclickable,
                  * or worse, clicking it also folds the panel.
                  *
                  * ⭐ AND IT CARRIES THE SAME AMBER DOT as Driver Check-ins: a
                  * filter that is on while the strip is CLOSED is invisible, and an
                  * invisible filter is how a scheduler concludes a message was never
                  * sent. ⛔ Do not drop the dot to "match" a simpler panel.
                  */}
                <button
                    type="button"
                    onClick={() => {
                        /**
                         * ---- ⛔⛔ IT OPENS THE PANEL TOO (Pete, 2026-09-09) ------
                         *
                         * > *"When you click the search button on the labor and comm
                         * > panels and the panels are collapsed, it does nothing.
                         * > should open the panel and show the search together"*
                         *
                         * ⚠️ THE STRIP LIVES INSIDE `{open && (...)}`, so toggling
                         * `showFilters` on a FOLDED card set a flag nobody could see.
                         * The click registered, the state changed, and the screen did
                         * not move - a control that does nothing, which is the exact
                         * failure this codebase keeps finding green.
                         *
                         * ⛔ OPENING ONLY, NEVER CLOSING. Hiding the strip must not
                         * fold the whole panel: a scheduler who has finished
                         * filtering still wants the list they were filtering.
                         */
                        const next = !showFilters;
                        setShowFilters(next);
                        if (next) setOpen(true);
                    }}
                    aria-expanded={showFilters}
                    aria-label={showFilters ? 'Hide search and filters' : 'Show search and filters'}
                    title={filtersActive && !showFilters
                        ? 'Filters are on - open to see or clear them'
                        : showFilters ? 'Hide search and filters' : 'Search and filter'}
                    className="relative shrink-0 self-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                    <Search className="h-3.5 w-3.5" />
                    {filtersActive && (
                        <span
                            aria-hidden="true"
                            className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                        />
                    )}
                </button>

                {/**
                  * ---- ⭐⭐ ALL — Pete: "next to the search icon" ---------------
                  *
                  * ⛔ IT IS THE WORD, NOT A GLYPH, AND THAT IS DELIBERATE. Earlier
                  * the same day the door-sort reset shipped as a bare ↺ whose only
                  * explanation was its tooltip, and Pete could not tell what it
                  * did — *"The Sort doesn't put them back to the orignal order"*.
                  * ⚠️ An unlabelled icon that undoes a state you cannot see is the
                  * exact shape of that problem. "ALL" is the word he used for it.
                  *
                  * ⭐ SHOWN ONLY WHEN A SCOPE IS ON, because with nothing latched
                  * it would be a control that does nothing — the same rule the
                  * door-order reset follows. ⚠️ It appears immediately beside the
                  * scope chip, so the thing to undo and the way to undo it are
                  * never more than a few pixels apart.
                  */}
                {/* ⛔ INCREMENT 3 — A PLAIN LABEL, NOT A BUTTON. The five per-panel `ALL`
                buttons are gone; one master reset at the top of the rail
                replaces them. ** A LABEL IS AN INDICATOR AND MUST READ IN
                EVERY STATE; A RESET IS A CONTROL AND MUST NOT BE ON SCREEN
                WHEN IT WOULD DO NOTHING. **
                ⚠️ Measured before the change: all four panels already share
                ONE focus, so every `ALL` already reset all four -- five
                controls for one action, and nothing said so. */}
                <span
                    title={focus ? `Showing only ${focusBasisLabel(focus)}` : 'Showing everything today'}
                    className="shrink-0 self-center text-[10px] leading-none text-muted-foreground"
                >
                    {focusScopeLabel(focus)}
                </span>

                {/**
                  * ---- ⭐ THE COUNT, MOVED RIGHT (Pete, 2026-09-09) ---------------
                  *
                  * > *"move the 17 (number of commuications) to the right, before the
                  * > refresh button"*
                  *
                  * ⭐ `ml-auto` PUSHES IT, so the title and the search icon stay
                  * together on the left and the count sits against the refresh
                  * button — the same shape Driver Check-ins has.
                  *
                  * ⛔ FAILURES STAY IN THE HEADER, so a FOLDED panel still says
                  * something is wrong. A fault visible only after you expand is a
                  * fault nobody sees.
                  */}
                {!loading && (
                    <span
                        className="ml-auto shrink-0 text-xs text-muted-foreground"
                        title={`Messages in the last ${HOURS} hours`}
                    >
                        {rows.length}
                        {failures > 0 && (
                            <span className="ml-1 font-medium text-rose-700">
                                · {failures} failed
                            </span>
                        )}
                    </span>
                )}

                {/* ⭐ REFRESH, MATCHING Scanned Paperwork (2026-08-27). Pete: *"For the
                    labor slider on right hand side, along with Communications, they both
                    need the refresh buttons like Scanned Paperwork has."*
                    ⚠️ ~~ONLY WHEN THE FEED IS ON~~ — it is always on now, so the
                    condition went with the badge. */}
                <button
                    type="button"
                    onClick={() => { void load(); }}
                    disabled={loading}
                    className="rounded p-0.5 hover:bg-muted disabled:opacity-40"
                    title="Re-read the last 24 hours now"
                >
                    <RefreshCw className="h-3 w-3" />
                </button>
            </div>

            {/* ⭐ ONLY THE BODY SCROLLS — see the note on the header above. */}
            {open && (
                <div className="min-h-0 overflow-y-auto px-3 py-2">
                    {(
                        <>
                            {/* ⛔ THE "All facilities" TICK-BOX WAS HERE. See the note by
                                the state it used to hold — this panel is scoped to the
                                facility on screen and there is no control to widen it. */}
                            {/**
                              * ⭐⭐ THE FILTER ROW (2026-08-27). One box and one toggle.
                              *
                              * ⭐ "FAILED ONLY" IS A ONE-CLICK TOGGLE BECAUSE IT IS THIS
                              * PANEL'S WHOLE REASON TO EXIST. It was built because the
                              * Automated Texts screen read "0 sent / 0 failed" and could
                              * not distinguish "nothing was due" from "everything is being
                              * refused" — so the failures are the thing somebody opens
                              * this to find, and they should not have to type to find them.
                              *
                              * ⚠️ 'Opted out' AND 'Already sent' ARE NOT FAILURES and this
                              * toggle does not show them. Both are the system working
                              * correctly, and lumping them in would send a scheduler
                              * chasing a delivery that was correctly withheld.
                              */}
                            {showFilters && (
                            <div className="mb-2 flex flex-wrap items-center gap-1.5">
                                <input
                                    type="search"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Appointment, PO, container, ref, last 4…"
                                    aria-label="Filter communications"
                                    className="min-w-0 flex-1 rounded border bg-background px-1.5 py-0.5 text-[11px]"
                                />
                                <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
                                    <input
                                        type="checkbox"
                                        checked={failedOnly}
                                        onChange={(e) => setFailedOnly(e.target.checked)}
                                    />
                                    Failed only
                                </label>
                            </div>
                            )}

                            {/**
                              * ⛔ A FILTERED LIST SAYS WHAT IT IS HIDING. A shorter list with
                              * no explanation is how somebody concludes a message was never
                              * sent — which is the exact wrong conclusion this panel exists
                              * to prevent.
                              */}
                            {/**
                              * ---- ⭐⭐ THE SCOPE, AND WHAT IT MATCHED ON ----------------
                              *
                              * ⛔ IT NAMES THE BASIS, NOT JUST THE TARGET. For a load that
                              * is the load number; for a driver with no load it is his
                              * masked tail, because THAT match is a heuristic on a masked
                              * string (see `commsMatchesFocus`). ⚠️ A heuristic that does
                              * not show its working is invisible when it is wrong — this
                              * line is how a scheduler spots somebody else's messages.
                              *
                              * ⛔⛔ AND IT SAYS "of the last N hours". Pete chose to filter
                              * what is already loaded rather than re-query, so a message
                              * older than the window genuinely cannot appear here. Without
                              * this sentence its absence reads as "never sent".
                              */}
                            {/* ⛔⛤ ~~THE GREY SCOPE BOX~~ REMOVED 2026-09-19 (§0.3). Pete: *"I don't
                                    like the gray box inside the panel and then the same small text
                                    line underneath it saying the same thing."*
                                    ** A BOX THAT RESTATES ITS NEIGHBOURS IS NOT CONTEXT, IT IS
                                       NOISE. ** ✅ The blocked reason survives as a plain line --
                                    it is the sentence that stops an empty panel being read as
                                    "nothing was sent". */}
                                {/* ⛔⛔ `outOfScope` SURVIVES THE BOX, AND THAT IS NOT TIDYING.
                                    §1AR: *"an empty scoped panel has three causes and they need
                                    different answers"* — nothing sent, nothing in the loaded
                                    window, or this focus cannot be scoped. The count of messages
                                    that exist but belong to somebody ELSE is what separates the
                                    first from the second, and dropping it with the chrome would
                                    let a scheduler read "no messages" as "he was never contacted"
                                    and text him twice.
                                    ** WHEN YOU DELETE A CONTAINER, CHECK WHAT WAS ONLY EVER SAID
                                       INSIDE IT. ** */}
                                {(blockedReason || outOfScope > 0) && (
                                    <div className="mb-1.5 text-[11px] text-muted-foreground">
                                        {blockedReason ?? `${outOfScope} more in the last ${HOURS} hours, for others.`}
                                    </div>
                                )}

                            {boardScopeNote(boardHidden) && (
                                <div className="mb-1.5 text-[11px] text-muted-foreground">
                                    {boardScopeNote(boardHidden)}
                                </div>
                            )}
                            {hidden > 0 && (
                                <div className="mb-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                                    <span>{hidden} hidden by the filter.</span>
                                    <button
                                        type="button"
                                        onClick={() => { setFilter(''); setFailedOnly(false); }}
                                        className="underline"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}

                            {/**
                              * ⚠️ AND AN EMPTY RESULT IS DISTINGUISHED FROM AN EMPTY FEED.
                              * "Nothing has gone out" and "nothing matches what you typed"
                              * are different facts and only one of them is worrying.
                              */}
                            {/* ⛔ THE SCOPE BOX ABOVE ALREADY EXPLAINS AN EMPTY SCOPED LIST,
                                and two explanations of one emptiness is how a reader stops
                                believing either. This one is for the TYPED filter only. */}
                            {visible.length === 0 && inScope.length > 0 && (
                                <div className="mb-1.5 rounded border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
                                    Nothing matches that. {inScope.length} message{inScope.length === 1 ? '' : 's'}
                                    {focus ? ' in scope' : ` in the last ${HOURS} hours`}.
                                </div>
                            )}

                            {/**
                              * ---- ⛔ ~~"last 24h"~~ REMOVED 2026-09-09 -------------------
                              *
                              * > Pete: *"Lets remove 'last 24h' and free up that line"*
                              *
                              * ⚠️ IT WAS A WHOLE ROW FOR TWO WORDS, right-aligned, in a rail
                              * ~340px wide where the feed itself is the thing worth showing.
                              *
                              * ⛔⛔ BUT THE FACT IT CARRIED IS NOT GONE, AND MUST NOT BE.
                              * This feed is a 24-HOUR SLICE and the header count ("17") does
                              * not say so — read as a total, it means a scheduler concludes a
                              * message was never sent when it was simply sent yesterday. That
                              * is the exact wrong conclusion this panel exists to prevent.
                              *
                              * ⭐ SO THE WINDOW MOVED ONTO THE COUNT ITSELF as a `title` —
                              * the number being misread is the right thing to hang it on, and
                              * it costs no vertical space. The refresh button says the same
                              * ("Re-read the last 24 hours now") and both empty states spell
                              * it out — ⚠️ but only WHEN EMPTY, which is exactly when nobody
                              * is misreading a count. That gap is why the count needed its own.
                              *
                              * ⛔ DO NOT DELETE `HOURS` AS "unused". Three call sites read it.
                              */}
                            {/**
                              * ⛔ THE FEED BEING UNREADABLE IS NOT THE SAME AS IT BEING
                              * EMPTY, and the two must never render the same way. This
                              * is the exact confusion the panel was built to end.
                              */}
                            {unavailable && (
                                <div className="mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                                    <span>The feed could not be read: {unavailable}</span>
                                </div>
                            )}
                            {error && !unavailable && (
                                <div className="mb-2 rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-900">
                                    {error}
                                </div>
                            )}

                            {!unavailable && !error && rows.length === 0 && !loading && (
                                <p className="text-xs text-muted-foreground">
                                    Nothing has gone out in the last {HOURS} hours
                                    for this facility.
                                </p>
                            )}

                            <ul className="space-y-1.5">
                                {visible.map(r => {
                                    const look = outcomeLook(r.outcome);
                                    const Icon = r.channel === 'email' ? Mail : MessageSquare;
                                    /**
                                     * ⭐ THE SAME SURFACE AS `LabourRow` AND THE DRIVER CARDS
                                     * (2026-08-27). Pete: *"a very slight grey background and a
                                     * border around each one… Should be the same for
                                     * communications too."*
                                     *
                                     * ⚠️ IT WAS `bg-card` — the same colour as the rail behind it,
                                     * so the border was doing all the work on its own. The fill is
                                     * what separates a stack of short rows at a glance.
                                     *
                                     * ⛔ A FAILED MESSAGE IS STILL MARKED BY ITS CHIP, NOT BY THIS
                                     * FILL. Tinting the whole row red would make one bad send shout
                                     * louder than the ten around it that also matter, and the
                                     * outcome chip already says it in words.
                                     *
                                     * ⚠️ AND THIS COMMENT SITS OUTSIDE THE `return (` DELIBERATELY.
                                     * A `{/* … *\/}` block as the first thing inside it is a SECOND
                                     * root element — the build said "Expected corresponding JSX
                                     * closing tag for 'section'" 100 lines further down, which is
                                     * not where the mistake was.
                                     */
                                    return (
                                        <li
                                            key={r.notificationSentId}
                                            /**
                                              * ⭐⭐ #67 — THE ROW UNDER THE CURSOR SAYS SO. The same
                                              * two tones `PaperworkTrayCard.tsx` already uses, which
                                              * is the panel Pete named as the example. One highlight
                                              * vocabulary across the rails, not a second one that
                                              * means the same thing in a different colour.
                                              *
                                              * ---- ⛔⛤ ~~CSS ONLY~~ LIFTED 2026-09-23 -------------
                                              *
                                              * This row used to carry: *"CSS ONLY. A rail row that
                                              * REPORTS a hover re-scopes the rail and rebuilds the row
                                              * under the cursor — A HOVER THAT RE-SCOPES THE LIST
                                              * CONTAINING THE HOVERED ROW IS A LOOP BY CONSTRUCTION."*
                                              *
                                              * ⭐ THE LOOP WAS REAL AND IT WAS FIXED ON 2026-09-21,
                                              * two days before this comment was read again. `Calendar`
                                              * gained `hoverApptOrigin`, and `previewFocus` returns
                                              * null for a rail-origin hover — so the panels stay at
                                              * (ALL), this list is never re-queried, and the row under
                                              * the cursor is never rebuilt. `LabourPanel` was moved
                                              * onto that mechanism; this row kept the ban.
                                              *   ** A PROHIBITION OUTLIVES ITS REASON UNLESS SOMEBODY
                                              *      DELETES IT — AND IT READS AS CURRENT, BECAUSE IT
                                              *      EXPLAINS ITSELF. **
                                              *
                                              * ⛔⛔ THE `'rail'` ARGUMENT IS LICENSING THE WHOLE THING.
                                              * Dropping it defaults to `'grid'`, `previewFocus` builds
                                              * a focus, every panel re-scopes — and the loop comes
                                              * straight back. It is not a label. It is the guard.
                                              */
                                            onMouseEnter={() => r.appointmentId
                                                && onHoverAppointment?.(r.appointmentId, 'rail')}
                                            onMouseLeave={() => r.appointmentId
                                                && onHoverAppointment?.(null, 'rail')}
                                            /* ⭐⭐ SINGLE CLICK MAKES THE HOVER STAY
                                               (Pete, 2026-09-27). ⛔ GUARDED ON THE ID like
                                               the two handlers above: an ad-hoc text to a
                                               driver with no load has no appointment to
                                               scope to, and latching null would clear a
                                               scope the scheduler chose.
                                                 ** A CONTROL THAT CANNOT ACT MUST DO
                                                    NOTHING, NOT UNDO. ** */
                                            onClick={() => r.appointmentId
                                                && onChooseAppointment?.(r.appointmentId)}
                                            /* ⚠️ THE LIGHTER RING, NOT THE FULL-STRENGTH ONE. Same
                                               vocabulary as the driver cards and the grid chips:
                                               `ring-slate-900` is "I chose this", `ring-slate-900/40`
                                               is "my cursor is here". A message is never SELECTED, so
                                               only the weak tone can ever appear here. */
                                            className={`rounded border border-border bg-muted/30 px-2 py-1.5
                                                       transition-colors hover:border-primary/50 hover:bg-primary/5
                                                       ${r.appointmentId && highlightAppointmentId === r.appointmentId
                                                           ? 'ring-2 ring-inset ring-slate-900/40' : ''}`}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                                                <span className="text-xs font-medium">
                                                    {typeLabel(r.typeCode)}
                                                </span>
                                                {r.appointmentNumber && (
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {r.appointmentNumber}
                                                    </span>
                                                )}
                                                <span className="ml-auto text-[11px] text-muted-foreground">
                                                    {hhmm(r.createdUtc)}
                                                </span>
                                                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${look.cls}`}>
                                                    {look.label}
                                                </span>
                                            </div>

                                            {r.recipients.length > 0 && (
                                                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                                    {r.recipients.join(', ')}
                                                </div>
                                            )}

                                            {/* ⭐ THE MESSAGE ITSELF, because "sent" is not
                                              * the same as "said the right thing" — a door
                                              * text naming the wrong door is a successful
                                              * send and a failed communication. */}
                                            {(r.subject || r.body) && (
                                                <div className="mt-0.5 line-clamp-2 text-[11px] text-foreground/80">
                                                    {r.subject ? <span className="font-medium">{r.subject}: </span> : null}
                                                    {r.body}
                                                </div>
                                            )}

                                            {/* ⛔ KEEP THE REASON. Without it "the texts are
                                              * failing" cannot be turned into WHY without
                                              * reproducing it — exactly where this left
                                              * Pete on 2026-08-20. */}
                                            {look.bad && r.errorDetail && (
                                                <div className="mt-0.5 text-[11px] text-rose-800">
                                                    {r.errorDetail}
                                                    {r.attemptCount > 1 && ` (${r.attemptCount} attempts)`}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>

                            {/**
                              * ⛔ NO SILENT CAPS, AND NOTHING HIDDEN WITHOUT A COUNT.
                              * The same rule the search panel follows: a row that
                              * exists and is not shown is reported, or the screen is
                              * quietly lying about what happened.
                              */}
                            {(capped || unattributed > 0) && (
                                <p className="mt-2 text-[11px] text-muted-foreground">
                                    {capped && `Showing the newest ${rows.length}. `}
                                    {/**
                                      * ⚠️ THE COUNT STAYS, THE INVITATION GOES. It used to
                                      * end "tick All facilities to include them" — and that
                                      * tick-box is gone, deliberately (§1K).
                                      *
                                      * ⛔ THE NUMBER IS STILL REPORTED because the rule is
                                      * "a row that exists and is not shown must be COUNTED,
                                      * or the screen is quietly lying". ⭐ But it is now
                                      * stated as a FACT rather than an offer: these are
                                      * other sites' messages and this screen will not show
                                      * them.
                                      */}
                                    {unattributed > 0 && (
                                        <>
                                            {unattributed} more went out in this window at
                                            other facilities, or were not tied to an
                                            appointment. They are not shown here.
                                        </>
                                    )}
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
